import { actionsKeys, capitalize, getRequiredFields, permissionActions } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";
import { toast } from "@/components/Toast";
import {
  useGetBillingCategoryByCommunity,
  useGetDocumentTypeDropdown,
  useGetGlMasterData,
  useGetParkedDocById,
  useGetPaymentTerms,
  useGetPropertyModelsByCommunity,
  usePostDocMutation,
  useUpdateBillingDocMutation,
} from "@/services";
import {
  BASELINE_DATE,
  BILLING_AMOUNT,
  BILLING_CATEGORY,
  BILLING_CATEGORY_ID,
  BILLING_DATE,
  BILLING_MONTH_YEAR,
  BUDGET_END_MONTH,
  BUDGET_START_MONTH,
  BUDGET_YEAR,
  CHECK,
  COMMENT,
  CURR_OWNER_ID,
  CURR_OWNER_NAME,
  DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  DUE_DATE,
  GL_ACCOUNT,
  GL_MASTER_ID,
  INVOICE_DATE,
  JE_MASTER_ID,
  LINE_BILLING_AMOUNT,
  LINE_ITEM_TYPE,
  LINE_ITEMS,
  PAYMENT_TERM,
  POSTING_DATE,
  PROPERTY_BLOCK,
  PROPERTY_ID,
  PROPERTY_MODEL,
  PROPERTY_OWNERSHIP_ID,
  STREET_ADDRESS,
  TRANSACTION_TYPE,
  UNIT_NO,
} from "./consts";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

const useAddEdit = () => {
  const { billingId, action } = useParams();
  const isView = action == routes.view;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterCriteria, setFilterCriteria] = useState({
    categoryId: "",
    modelId: "",
  });
  const [keys, setKeys] = useState({
    accountName: Math.random() * 2,
    documentTypes: Math.random() * 2,
    paymentTerm: Math.random() * 2,
    billingCategory: Math.random() * 2,
    propertyModel: Math.random() * 2,
    transactionType: Math.random() * 2,
  });
  const [openPostDialog, setOpenPostDialog] = useState<boolean>(false);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const hasPostPermission = useMemo(
    () => accessPermission?.[actionsKeys?.BILLING_DOCUMENT?.toLowerCase()]?.[permissionActions.POST],
    [accessPermission]
  );

  const hasUpdatePermission = useMemo(
    () => accessPermission?.[actionsKeys?.BILLING_DOCUMENT?.toLowerCase()]?.[permissionActions.UPDATE],
    [accessPermission]
  );

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE]: "",
      [DOCUMENT_ID]: "",
      [BILLING_MONTH_YEAR]: "",
      [BILLING_DATE]: "",
      [BILLING_AMOUNT]: "",
      [DUE_DATE]: "",
      [TRANSACTION_TYPE]: "",
      [GL_ACCOUNT]: "",
      [PAYMENT_TERM]: "",
      [COMMENT]: "",
      [POSTING_DATE]: "",
      // [POSTING_MONTH]: "",
      // [YEAR]: "",
      [LINE_ITEMS]: [
        {
          [BILLING_CATEGORY]: "",
          [PROPERTY_MODEL]: "",
          [PROPERTY_BLOCK]: "",
          [PROPERTY_ID]: "",
          [UNIT_NO]: "",
          [STREET_ADDRESS]: "",
          [CURR_OWNER_ID]: "",
          [CURR_OWNER_NAME]: "",
          [DRCR]: "",
          [LINE_BILLING_AMOUNT]: "",
          [CHECK]: false,
        },
      ],
    },
    resolver: zodResolver(schema),
  });
  const buttonAction = useRef<string>("park");
  const requiredFields = getRequiredFields(schema);
  const formData = useWatch({control: form.control})

  const breadcrumbData = useMemo(() => {
    return [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.billing}`,
        name: "Billing",
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.billing}/${routes.pendingPosting}`,
        name: <span>{"Pending for Posting"}</span>,
      },
      {
        link: "",
        name: (
          <span className="text-primary">{capitalize(action as string)}</span>
        ),
      },
    ];
  }, []);

  const breadcrumbTitle = `Pending for Posting - ${communityDetail?.communityName}`;

  const onBackClick = () => {
    navigate(
      `/${routes.bom}/${routes.finance}/${routes.billing}/${routes.pendingPosting}`
    );
  };

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: paymentTermOptions, isLoading: isPaymentTermLoading } =
    useGetPaymentTerms();

  const { data: propertyModels, isLoading: isPropertyModelLoading } =
    useGetPropertyModelsByCommunity({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      }
    });

  const { data: billingCategory, isLoading: isBillingCategoryLoading } =
  useGetBillingCategoryByCommunity({communityId: communityDetail?.communityId});

  const { mutate: postInfo, isPending: isPostingPending } =
    usePostDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: ({data}: any) => {
        setOpenPostDialog(false);
        queryClient.invalidateQueries({
          queryKey: ["park-documents"],
        });
        onBackClick();
        toast.success(`Document (Doc ID: ${data?.data?.postDocumentHeaderId}) Posted Successfully`);
      },
    });

    const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateBillingDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: ({data}: any) => {
        if (buttonAction.current == "post") {
          postInfo({ communityId: communityDetail?.communityId, parkDocId: billingId as string });
        } else {
          queryClient.invalidateQueries({
            queryKey: ["park-documents"],
          });
          onBackClick();
          toast.success(`Document (Doc ID: ${data?.data?.header?.parkDocumentHeaderId}) Updated Successfully`);
        }
      },
    });
    
  useEffect(() => {
    if (documentTypes) {
      if (!billingId) {
        const documentTypeId = documentTypes
          ?.find((data: any) => {
            return data?.documentType?.toLowerCase() == "ob";
          })
          ?.documentTypeId?.toString();
        if (documentTypeId) {
          form.setValue(DOCUMENT_TYPE, documentTypeId);
        } else {
          toast.error(
            "The Document Type 'OB' was not found. Kindly add it to proceed."
          );
          onBackClick();
        }
      }
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 4 }));
    }
  }, [documentTypes]);

  useEffect(() => {
    if (paymentTermOptions) {
      setKeys((prev: any) => ({ ...prev, paymentTerm: Math.random() * 4 }));
    }
  }, [paymentTermOptions]);

  const { data: pendingBillingData, isLoading: isLoadingBillingData } =
    useGetParkedDocById({
      id: billingId as string,
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!(billingId && communityDetail?.communityId),
      },
    });

  useEffect(() => {
    if (pendingBillingData) {
      const header = pendingBillingData?.data?.header;
      form.reset({
        ...header,
        [JE_MASTER_ID]: header?.[JE_MASTER_ID]?.toString(),
        [DOCUMENT_TYPE]:
          header?.[DOCUMENT_TYPE]?.toString() || "",
        [DOCUMENT_ID]:
          header?.[DOCUMENT_ID]?.toString() ||
          header?.parkedDocumentHeaderId?.toString() ||
          billingId?.toString() ||
          "",
        [BILLING_MONTH_YEAR]:
          `${header?.postingPeriod?.toString()}/${header?.postingYear?.toString()}` ||
          "",
        [BILLING_DATE]:
          header?.[
            TRANSACTION_TYPE
          ]?.toUpperCase() == "MONTHLY_HOA_FEE" && header?.createdAt
            ? header?.createdAt?.toString()
            : header?.billingDate
              ? header?.billingDate?.toString()
              : "",
        [BILLING_AMOUNT]:
          header?.approvedAmount?.toString() || "",
        [DUE_DATE]: header?.[DUE_DATE]
          ? moment(header?.[DUE_DATE]).toString()
          : "",
        [BASELINE_DATE]: header?.[BASELINE_DATE]
          ? moment(header?.[BASELINE_DATE]).toString()
          : "",
        [TRANSACTION_TYPE]: header?.[TRANSACTION_TYPE]?.toString() || "",
        [GL_ACCOUNT]: header?.glMasterId?.toString() || "",
        [PAYMENT_TERM]: header?.[PAYMENT_TERM]?.toString() || "",
        [COMMENT]: header?.comments?.toString() || "",
        [POSTING_DATE]: header?.[POSTING_DATE]
          ? moment(header?.[POSTING_DATE])?.toString()
          : "",
        [LINE_ITEMS]: pendingBillingData?.data?.lineItems?.map((item: any) => ({
          ...item, 
          [BILLING_CATEGORY]: item?.billingCategory || "",
          [PROPERTY_MODEL]: item?.propertyModelName?.toString() || "",
          [PROPERTY_BLOCK]: item?.[PROPERTY_BLOCK]?.toString() || "",
          [PROPERTY_ID]: item?.[PROPERTY_ID]?.toString() || "",
          [UNIT_NO]: item?.unitNo?.toString() || "",
          [STREET_ADDRESS]:
            item?.streetAddress?.toString() || "",
          [CURR_OWNER_ID]:
            item?.[CURR_OWNER_ID]?.toString() || "",
          [CURR_OWNER_NAME]:
            item?.ownershipName?.toString() || "",
          [DRCR]: item?.drCr?.toString() || "",
          [LINE_BILLING_AMOUNT]: item?.[LINE_BILLING_AMOUNT]?.toString() || "",
          [PROPERTY_OWNERSHIP_ID]: item?.[CURR_OWNER_ID]?.toString() || "",
          [GL_MASTER_ID]: item?.[GL_MASTER_ID]?.toString() || "",
          [BILLING_CATEGORY_ID]: item?.[BILLING_CATEGORY_ID]?.toString() || "",
          [BUDGET_START_MONTH]: item?.[BUDGET_START_MONTH]?.toString() || "",
          [BUDGET_END_MONTH]: item?.[BUDGET_END_MONTH]?.toString() || "",
          [BUDGET_YEAR]: item?.[BUDGET_YEAR]?.toString() || "",
          [CHECK]: item?.check || false,
        })),
      });

      setKeys({
        accountName: Math.random() * 2,
        documentTypes: Math.random() * 2,
        paymentTerm: Math.random() * 2,
        billingCategory: Math.random() * 2,
        propertyModel: Math.random() * 2,
        transactionType: Math.random() * 2,
      });
    }
  }, [pendingBillingData]);

  const onChangeFilterCriteria = (key: string, value: string) => {
    setFilterCriteria((prev) => ({ ...prev, [key]: value }));
  };

  // Helper function to map a single raw API item into form field structure
  const mapLineItemToForm = (item: any) => ({
    ...item,
    [BILLING_CATEGORY]: item?.billingCategory || "",
    [PROPERTY_MODEL]: item?.propertyModelName?.toString() || "",
    [PROPERTY_BLOCK]: item?.[PROPERTY_BLOCK]?.toString() || "",
    [PROPERTY_ID]: item?.[PROPERTY_ID]?.toString() || "",
    [UNIT_NO]: item?.unitNo?.toString() || "",
    [STREET_ADDRESS]:
      item?.streetAddress?.toString() || "",
    [CURR_OWNER_ID]:
      item?.[CURR_OWNER_ID]?.toString() || "",
    [CURR_OWNER_NAME]:
      item?.ownershipName?.toString() || "",
    [DRCR]: item?.drCr?.toString() || "",
    [LINE_BILLING_AMOUNT]: item?.[LINE_BILLING_AMOUNT]?.toString() || "",
    [CHECK]: item?.check || false,
  });

  const onApplyFilter = () => {
    const rawItems = pendingBillingData?.data?.lineItems || [];
    
    const { categoryId, modelId } = filterCriteria || {};

    // If no criteria selected, return all mapped items instead of empty
    if (!categoryId && !modelId) {
      form.setValue(LINE_ITEMS, rawItems.map(mapLineItemToForm)?.filter(Boolean), {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }

    const filtered = rawItems.filter((item: any) => {
      const matchesCategory = categoryId
        ? item?.property?.categoryId?.toString() === categoryId?.toString()
        : true;

      const matchesModel = modelId
        ? item?.property?.modelId?.toString() === modelId?.toString()
        : true;

      return matchesCategory && matchesModel;
    });

    // Set formatted line items into form state
    form.setValue(LINE_ITEMS, filtered.map(mapLineItemToForm)?.filter(Boolean), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onClearFilter = () => {
    setFilterCriteria({
      categoryId: "",
      modelId: "",
    });

    const rawItems = pendingBillingData?.data?.lineItems || [];

    form.setValue(LINE_ITEMS, rawItems.map(mapLineItemToForm), {
      shouldValidate: true,
      shouldDirty: true,
    });

    setKeys((prev: any) => ({
      ...prev,
      billingCategory: Math.random() * 2,
      propertyModel: Math.random() * 2,
    }));
  };

  const watchedLineItems = useWatch({ control: form.control });

  const balanceAmount = useCallback(() => {
    if (!Array.isArray(watchedLineItems)) return 0;

    // 2. Optimized single-pass calculation
    const totals = watchedLineItems?.filter((item: any) => item?.[LINE_ITEM_TYPE] == "MAIN")?.reduce((acc, item) => {
      const amt = Number(item?.amount) || 0;
      const type = item?.drCr?.toLowerCase();

      if (type == "dr") acc.dr += amt;
      if (type == "cr") acc.cr += amt;
      
      return acc;
    }, { dr: 0, cr: 0 });

    return totals.dr - totals.cr - formData[BILLING_AMOUNT];
    
    // 3. Dependency array is fine, but lineItems MUST be from form.watch()
  }, [watchedLineItems]);
  
  const onSubmit = (data: any) => {
    if (balanceAmount() != 0) {
      toast.error(
        "Billing Amount and Line Item amount should match"
      );
      return;
    }
    
    if (buttonAction.current == "post") {
      setOpenPostDialog(true);
    } else {
      updateInfo({
        communityId: communityDetail?.communityId,
        data: {
          [JE_MASTER_ID]: data[JE_MASTER_ID],
          header: {
            [POSTING_DATE]: data?.[POSTING_DATE] ? moment(data?.[POSTING_DATE]).format("YYYY-MM-DD") : "",
            [INVOICE_DATE]: data?.[INVOICE_DATE] ? moment(data?.[INVOICE_DATE]).format("YYYY-MM-DD") : "",
            [BASELINE_DATE]: data?.[BASELINE_DATE] ? moment(data?.[BASELINE_DATE]).format("YYYY-MM-DD") : "",
            [DUE_DATE]: data?.[DUE_DATE] ? moment(data?.[DUE_DATE]).format("YYYY-MM-DD") : "",
            [PAYMENT_TERM]: data?.[PAYMENT_TERM],
            [COMMENT]: data?.[COMMENT],
            approvedAmount: data[BILLING_AMOUNT]
          },
          [LINE_ITEMS]: data[LINE_ITEMS]?.filter((item: any) => item?.[LINE_ITEM_TYPE] == "MAIN")
        }
      })
    }
  };

  const onPostConfirmation = () => {
    updateInfo({
      communityId: communityDetail?.communityId,
      data: {
        [JE_MASTER_ID]: formData?.[JE_MASTER_ID],
        header: {
          [POSTING_DATE]: formData?.[POSTING_DATE] ? moment(formData?.[POSTING_DATE]).format("YYYY-MM-DD") : "",
          [INVOICE_DATE]: formData?.[INVOICE_DATE] ? moment(formData?.[INVOICE_DATE]).format("YYYY-MM-DD") : "",
          [BASELINE_DATE]: formData?.[BASELINE_DATE] ? moment(formData?.[BASELINE_DATE]).format("YYYY-MM-DD") : "",
          [DUE_DATE]: formData?.[DUE_DATE] ? moment(formData?.[DUE_DATE]).format("YYYY-MM-DD") : "",
          [PAYMENT_TERM]: formData?.[PAYMENT_TERM],
          [COMMENT]: formData?.[COMMENT],
          approvedAmount: formData[BILLING_AMOUNT]
        },
        [LINE_ITEMS]: formData[LINE_ITEMS]?.filter((item: any) => item?.[LINE_ITEM_TYPE] == "MAIN")
      }
    })
  };

  return {
    breadcrumbData,
    onBackClick,
    communityDetail,
    form,
    requiredFields,
    glData,
    isDocumentTypeLoading,
    documentTypes,
    isLoadingGlMaster,
    onCancel: onBackClick,
    breadcrumbTitle,
    keys,
    onSubmit,
    isPaymentTermLoading,
    paymentTermOptions,
    propertyModels,
    isPropertyModelLoading,
    billingCategory,
    isBillingCategoryLoading,
    onChangeFilterCriteria,
    onApplyFilter,
    onClearFilter,
    filterCriteria,
    isLoadingBillingData,
    openPostDialog,
    setOpenPostDialog,
    onPostConfirmation,
    isPostingPending,
    hasPostPermission,
    isView,
    buttonAction,
    hasUpdatePermission,
    isUpdatePending
  };
};

export default useAddEdit;
